import type { AuthorizedSigner, AuthorizedSignerForm } from '../../services/authorizedSignerService';
import { normalizeSimPhone } from '../../utils/simPhone';

/**
 * ฟอร์มผู้มีอำนาจลงนาม — ใช้ร่วมกันทั้งหน้าดู (AuthorizedSignerViewPage) และหน้าเพิ่ม/แก้ไข (AuthorizedSignerFormPage)
 * ยกมาจากโมดัลเดิมใน AuthorizedSignerPage (มติ PO 28 ต.ค. 2026 — ดู/แก้ไขเป็นหน้าเต็ม)
 */

export const emptyAuthorizedSignerForm = (companyId = 0): AuthorizedSignerForm => ({
  company_id: companyId,
  full_name: '',
  position: '',
  email: '',
  phone: '',
  is_active: true,
  is_primary: false,
});

/** แถวจาก API → ค่าในฟอร์ม (ตัวเดียวทั้งหน้าดูและหน้าแก้ไข) */
export const authorizedSignerToForm = (s: AuthorizedSigner): AuthorizedSignerForm => ({
  company_id: s.company_id,
  full_name: s.full_name || s.name || '',
  position: s.position || '',
  email: s.email || '',
  phone: s.phone || '',
  is_active: s.is_active,
  is_primary: s.is_primary || false,
});

/**
 * payload ของ PUT — เฉพาะช่องที่แก้ได้บนฟอร์มแก้ไข
 * `company_id` ไม่ส่ง: AuthorizedSignerUpdate (backend) ไม่มีฟิลด์นี้ — โมดัลเดิมให้เลือกบริษัทใหม่ได้แต่ server
 * เมินเงียบ (จอบอกว่าย้ายบริษัทแล้ว ทั้งที่ไม่ย้าย) · หน้าแก้ไขจึงแสดงบริษัทแบบอ่านอย่างเดียว
 */
export const authorizedSignerUpdatePayload = (f: AuthorizedSignerForm): Partial<AuthorizedSignerForm> => ({
  full_name: f.full_name,
  position: f.position,
  email: f.email,
  phone: f.phone,
  is_active: f.is_active,
  is_primary: f.is_primary,
});

/** token กันแก้ทับ (BUG-S162-P1) — updated_at ที่โหลดมา (ไม่เคยแก้ = created_at) */
export const authorizedSignerIfMatch = (s: AuthorizedSigner): string | undefined =>
  s.updated_at || s.created_at || undefined;

/** ตรวจฟอร์มก่อนยิง API (ตรรกะเดียวกับ validateForm ของโมดัลเดิม · เบอร์ตรงกับ backend validate_thai_phone) */
export function validateAuthorizedSignerForm(f: AuthorizedSignerForm): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!f.company_id || f.company_id === 0) errors.company_id = 'กรุณาเลือกบริษัท';
  if (!f.full_name.trim()) errors.full_name = 'กรุณาใส่ชื่อ-นามสกุล';
  if (!f.position.trim()) errors.position = 'กรุณาใส่ตำแหน่ง';
  if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
  if (f.phone) {
    // +66/0066 → 0 ให้ตรงกับ backend validate_thai_phone (KD3 S2501)
    const clean = normalizeSimPhone(f.phone).replace(/[-\s()]/g, '');
    const mobile = /^0[689]\d{8}$/;
    const landline = /^0[2-57]\d{7,8}$/;
    if (!mobile.test(clean) && !landline.test(clean)) errors.phone = 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง';
  }
  return errors;
}

/** ป้ายฟิลด์สำหรับไทม์ไลน์ประวัติกิจกรรม */
export const AUTHORIZED_SIGNER_FIELD_LABELS: Record<string, string> = {
  company_id: 'บริษัท',
  full_name: 'ชื่อ-นามสกุล',
  position: 'ตำแหน่ง',
  email: 'อีเมล',
  phone: 'เบอร์โทรศัพท์',
  is_active: 'สถานะ',
  is_primary: 'ผู้ลงนามหลัก',
};

export const AUTHORIZED_SIGNER_VALUE_FORMATTERS: Record<string, (v: unknown) => string> = {
  is_active: (v) => (v ? 'ใช้งาน' : 'ไม่ใช้งาน'),
  is_primary: (v) => (v ? 'ผู้ลงนามหลัก' : 'ไม่ใช่ผู้ลงนามหลัก'),
};
